import React, { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import axios from "axios";
import moment from "moment";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Moment from "react-moment";
import { Modal } from "react-bootstrap";

import ManageSidebar from "../ManageSidebar";
import CustomSpinLoader from "../../../../components/CustomSpinLoader";
import DataTable from "../../../../common/DataTable";
import { BASE_URL } from "../../../../../BaseUrl";
import { _t, customStyle, getCookie } from "../../../../../functions/Functions";
import { ToastError, ToastSuccess } from "../../../../../functions/ToastShow";
import { RestaurantContext } from "../../../../../contexts/Restaurant";

const ACTIONS = {
  process: {
    title: "Moving Avg",
    endpoint: "/settings/month-closing-stock/process",
    success: "Closing process completed",
    icon: "fa fa-line-chart",
    btnClass: "btn-primary",
  },
  post: {
    title: "Post",
    endpoint: "/settings/month-closing-stock/post",
    success: "Posted successfully",
    icon: "fa fa-check",
    btnClass: "btn-success",
  },
  foodCost: {
    title: "Food Cost",
    endpoint: "/settings/month-closing-food-cost",
    success: "Food cost processed",
    icon: "fa fa-cutlery",
    btnClass: "btn-info",
  },
};

const ACTION_ORDER = ["process", "post", "foodCost"];

const formatDate = (date) => {
  if (!date) return "";
  return moment(date).format("YYYY-MM-DD");
};

const formatNumber = (value) => {
  if (value == null || value === "") return "—";
  const num = Number(value);
  if (isNaN(num)) return String(value);
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
};

const parseList = (body) => {
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
};

const MovingAvg = () => {
  const { t } = useTranslation();
  const { branchForSearch } = useContext(RestaurantContext);

  const [loading, setLoading] = useState(false);
  const [branch, setBranch] = useState(null);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [rows, setRows] = useState([]);
  const [activeAction, setActiveAction] = useState(null);

  const actionConfig = activeAction ? ACTIONS[activeAction] : null;

  useEffect(() => {
    if (branch) return;
    if (!Array.isArray(branchForSearch) || !branchForSearch.length) return;
    setBranch(branchForSearch[0]);
  }, [branchForSearch, branch]);

  const openActionModal = (actionKey) => {
    setActiveAction(actionKey);
  };

  const closeActionModal = () => {
    if (loading) return;
    setActiveAction(null);
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (!actionConfig) return;

    if (!branch || branch.id == null) {
      ToastError({ title: _t(t("Please select a branch")) });
      return;
    }
    if (!fromDate || !toDate) {
      ToastError({ title: _t(t("Please select from date and to date")) });
      return;
    }

    const payload = {
      branch_id: branch.id,
      from_date: formatDate(fromDate),
      to_date: formatDate(toDate),
    };

    setLoading(true);
    try {
      const res = await axios.post(BASE_URL + actionConfig.endpoint, payload, {
        headers: { Authorization: "Bearer " + getCookie() },
      });

      const body = res && res.data != null ? res.data : null;
      setRows(parseList(body));
      ToastSuccess({ title: _t(t(actionConfig.success)) });
      setActiveAction(null);
    } catch (error) {
      setRows([]);
      let errMsg = error?.response?.data?.message || error?.response?.message || "Please try again";
      ToastError({ title: _t(t(errMsg)) });
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        label: _t(t("S.No")),
        minWidth: "50px",
        render: function SrCell(item, i) {
          return i + 1;
        },
      },
      {
        label: _t(t("Document")),
        render: function DocCell(item) {
          if (item && item.document_identity) return item.document_identity;
          if (item && item.document_no) return item.document_no;
          return "—";
        },
      },
      {
        label: _t(t("Closing Date")),
        render: function DateCell(item) {
          if (!item || !item.posting_date) return "—";
          const m = moment(item.posting_date, ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "YYYY/MM/DD"]);
          return m.isValid() ? m.format("DD-MMM-YYYY") : item.posting_date;
        },
      },
      {
        label: _t(t("Item")),
        key: "item_name",
      },
      {
        label: _t(t("Rate")),
        render: function RateCell(item) {
          return formatNumber(item ? item.rate : null);
        },
      },
      {
        label: _t(t("Moving Avg")),
        render: function AvgCell(item) {
          return formatNumber(item ? item.moving_avg : null);
        },
      },
      {
        label: _t(t("Stock Qty")),
        render: function QtyCell(item) {
          return formatNumber(item ? item.stock_qty : null);
        },
      },
      {
        label: _t(t("StockAmount")),
        render: function AmountCell(item) {
          return formatNumber(item ? item.stock_amount : null);
        },
      },
    ],
    [t]
  );

  return (
    <>
      <Helmet>
        <title>{t("Moving Avg")}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>

            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="fk-scroll--pos-menu table-bottom-info-hide" data-simplebar>
                  <div className="t-pl-15 t-pr-15">
                    <CustomSpinLoader loading={loading} />

                    <div className="d-flex flex-wrap align-items-center justify-content-between t-pt-15 t-pb-15" style={{ gap: "0.75rem" }}>
                      <h5 className="mb-0 font-weight-bold" style={{ color: "#1e293b" }}>
                        {_t(t("Moving Avg"))}
                      </h5>
                      <div
                        className="d-flex flex-wrap align-items-center"
                        style={{
                          gap: "0.4rem",
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "0.35rem",
                        }}
                      >
                        {ACTION_ORDER.map(function (actionKey) {
                          const action = ACTIONS[actionKey];
                          return (
                            <button
                              key={actionKey}
                              type="button"
                              className={
                                "btn btn-sm rounded-sm font-weight-bold px-3 d-flex align-items-center " +
                                action.btnClass
                              }
                              onClick={() => openActionModal(actionKey)}
                              disabled={loading}
                              style={{ minHeight: "32px", letterSpacing: "0.01em" }}
                            >
                              <i className={action.icon + " mr-2"} aria-hidden="true" />
                              {_t(t(action.title))}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <DataTable
                      columns={columns}
                      loading={loading}
                      data={rows}
                      emptyText={_t(t("No Data Found"))}
                      theadClass="fk-scroll--5-5--pos-table"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Modal
        show={Boolean(actionConfig)}
        onHide={closeActionModal}
        centered
        enforceFocus={false}
      >
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {_t(t(actionConfig ? actionConfig.title : "Closing Process"))}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="form-group mb-3">
              <label className="form-label mb-1">
                {_t(t("Branch"))}
                <span className="text-danger ml-1">*</span>
              </label>
              <Select
                options={branchForSearch || []}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.id}
                value={branch}
                styles={customStyle}
                classNamePrefix="select"
                className="table-text"
                onChange={(val) => setBranch(val)}
                maxMenuHeight="200px"
                placeholder={_t(t("Select branch")) + ".."}
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label mb-1">
                {_t(t("From date"))}
                <span className="text-danger ml-1">*</span>
              </label>
              <DatePicker
                selected={fromDate}
                onChange={(date) => setFromDate(date)}
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className="form-control"
                placeholderText={_t(t("From date"))}
                shouldCloseOnSelect={true}
                maxDate={new Date()}
              />
            </div>
            <div className="form-group mb-0">
              <label className="form-label mb-1">
                {_t(t("To date"))}
                <span className="text-danger ml-1">*</span>
              </label>
              <DatePicker
                selected={toDate}
                onChange={(date) => setToDate(date)}
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                className="form-control"
                placeholderText={_t(t("To date"))}
                shouldCloseOnSelect={true}
                maxDate={new Date()}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <button
              type="button"
              className="btn rounded hov-btn"
              onClick={closeActionModal}
              disabled={loading}
            >
              {_t(t("Cancel"))}
            </button>
            <button
              type="submit"
              className={
                "btn rounded font-weight-bold " +
                (actionConfig ? actionConfig.btnClass : "btn-primary")
              }
              disabled={loading}
            >
              {loading ? _t(t("Please wait")) + "..." : _t(t("Apply"))}
            </button>
          </Modal.Footer>
        </form>
      </Modal>
    </>
  );
};

export default MovingAvg;
